import React, { useEffect, useMemo, useState } from 'react';
import {
  Users, ShieldCheck, Lock, AlertTriangle, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { Card, Pill, MetricCard, EmptyState } from '../../index';
import { ScoreDistributionBars } from '../ScoreDistributionBars';
import { getTrustDashboard, getTrustProfiles } from '../../../../services/api';
import { publishedBand, bandTone, bandLabel } from '../../../../services/trustBands';

/**
 * DashboardTab — V2 Trust Dashboard (Phase A5 / G305).
 *
 * Full V1 feature parity:
 *   - Hero KPIs: Total Agents, Fleet Trust Score (colored), On Probation, Active Anomalies
 *   - Risk Level distribution (4 colored bars: LOW / MEDIUM / HIGH / CRITICAL)
 *   - Autonomy Level distribution (bars per level)
 *   - Score Distribution (4 buckets: 90-100 / 80-89 / 70-79 / <70)
 *   - Recent Autonomy Transitions table (Agent | From → To | Trigger | Timestamp)
 *
 * Server-side dashboard endpoint surfaces tenant-wide aggregates that
 * augment the client-side scope-aware computations. Both views are
 * presented side-by-side so the operator can compare "what I'm
 * looking at" vs "the org as a whole".
 *
 * @param {Object} props
 * @param {string} props.scope            — a key from utils/trustScope (all / let / autolet / extelet / syslet)
 * @param {Array} props.enriched          — scope-filtered agents w/ band derived
 * @param {Function} props.addToast
 */
/**
 * G327 — Robust apiFetch + Spring Pageable response normaliser.
 * apiFetch wraps every response as { ok, data }. Pageable endpoints
 * then wrap data as { content, totalElements, ... }. We need to
 * drill through both layers; previously V2 stopped at r.data and
 * never reached `content`, silently returning empty.
 */
function extractList(r) {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  const d = r.data;
  if (Array.isArray(d)) return d;
  for (const k of ['items', 'content', 'results', 'profiles', 'transitions']) {
    if (Array.isArray(r[k])) return r[k];
  }
  if (d && typeof d === 'object') {
    for (const k of ['content', 'items', 'results', 'profiles', 'transitions']) {
      if (Array.isArray(d[k])) return d[k];
    }
  }
  return [];
}

export function DashboardTab({ scope, enriched, addToast }) {
  /* Server dashboard, for the archetypes currently in scope. Used to populate the
   * "tenant-wide" panel. */
  const [serverData, setServerData] = useState(null);
  const [recentTransitions, setRecentTransitions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getTrustDashboard({ scope }).catch(() => null),
      /* Pull profiles so we can derive recent autonomy transitions
       * client-side from the autonomy history. The dashboard endpoint
       * doesn't currently include this. */
      getTrustProfiles().catch(() => null),
    ]).then(([dash, profiles]) => {
      if (cancelled) return;
      setServerData(dash?.data || dash || null);
      /* G327 — robust extraction handles Spring Pageable + apiFetch wrapping */
      const list = extractList(profiles);
      /* Flatten any recent transitions surfaced in the profiles. The
       * shape varies — backend may expose `recentTransitions` per
       * profile or a `lastTransitionAt` field. We accept both. */
      const transitions = [];
      for (const p of (list || [])) {
        if (Array.isArray(p.recentTransitions)) {
          for (const t of p.recentTransitions) {
            transitions.push({
              agentId: p.agentId || p.id,
              agentName: p.agentName || p.name,
              ...t,
            });
          }
        } else if (p.lastTransitionAt) {
          transitions.push({
            agentId: p.agentId || p.id,
            agentName: p.agentName || p.name,
            fromLevel: p.previousAutonomyLevel,
            toLevel: p.currentAutonomyLevel || p.autonomyLevel,
            triggerType: p.lastTransitionTrigger || 'auto',
            timestamp: p.lastTransitionAt,
          });
        }
      }
      transitions.sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setRecentTransitions(transitions.slice(0, 10));
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [scope]);

  /* ── KPIs ─────────────────────────────────────────────────────── */
  const kpis = useMemo(() => {
    // Only RATED agents (trust != null) contribute to the fleet average. Unrated
    // agents have no evidence-backed number — counting them (as 0 or a seed) would
    // be theater. fleetScore is null when nothing in scope is rated yet.
    const scores = enriched
      .filter((a) => a.trust != null)
      .map((a) => Number(a.trust))
      .filter(Number.isFinite);
    const fleetScore = scores.length > 0
      ? Math.round(scores.reduce((s, x) => s + x, 0) / scores.length)
      : null;
    const onProbation = enriched.filter((a) => a.probationUntil || a.autonomy === 'PROBATION').length;
    const anomalies = enriched.filter((a) => a.anomalyDetected || a.hasAnomaly).length;
    return {
      total: enriched.length,
      fleetScore,
      onProbation,
      anomalies,
    };
  }, [enriched]);

  /* ── Risk Level distribution ──────────────────────────────────── */
  const riskDistribution = useMemo(() => {
    const buckets = { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0, UNRATED: 0 };
    for (const a of enriched) {
      // Unrated (no governed evidence) is its own bucket — never default it to
      // MEDIUM or fold its null score into CRITICAL.
      if (a.trust == null) { buckets.UNRATED += 1; continue; }
      const s = Number(a.trust);
      const risk = (a.riskLevel || (
        !Number.isFinite(s) ? 'UNRATED'
        : s >= 90 ? 'LOW'
        : s >= 80 ? 'MEDIUM'
        : s >= 70 ? 'HIGH'
        : 'CRITICAL'
      )).toString().toUpperCase();
      buckets[risk] = (buckets[risk] || 0) + 1;
    }
    return buckets;
  }, [enriched]);

  /* ── Autonomy distribution ────────────────────────────────────── */
  const autonomyDistribution = useMemo(() => {
    const buckets = new Map();
    for (const a of enriched) {
      const lvl = a.autonomy || a.autonomyLevel || 'UNSET';
      buckets.set(lvl, (buckets.get(lvl) || 0) + 1);
    }
    return Array.from(buckets.entries()).sort((a, b) => b[1] - a[1]);
  }, [enriched]);

  /* ── Score distribution buckets ───────────────────────────────── */
  const scoreBuckets = useMemo(() => {
    let excellent = 0, good = 0, fair = 0, atRisk = 0;
    for (const a of enriched) {
      if (a.trust == null) continue; // unrated — no score to bucket
      const s = Number(a.trust);
      if (!Number.isFinite(s)) continue;
      /* The published bands, not a private set. These were 90/80/70, which agreed with no other
       * surface — an agent could sit in "good" here and read Adequate on its own scorecard. */
      const band = publishedBand(s);
      if (band === 'strong') excellent += 1;
      else if (band === 'adequate') good += 1;
      else if (band === 'limited') fair += 1;
      else atRisk += 1;
    }
    return { excellent, good, fair, atRisk };
  }, [enriched]);

  return (
    <div className="space-y-5">
      {/* Hero KPIs — MetricCard.icon expects a ReactNode (not a
          component class), so we render the lucide icons inline at
          the size the card slot accommodates. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard label="Total agents" value={kpis.total} tone="cyan" icon={<Users size={16} />} />
        <MetricCard
          label="Fleet trust score"
          value={kpis.fleetScore != null ? `${kpis.fleetScore}` : '—'}
          icon={<ShieldCheck size={16} />}
          tone={bandTone(kpis.fleetScore)}
          delta={{
            /* The band's own name. This said Excellent / Good / Fair, so the fleet number was
               described in words that appear nowhere else a score is shown. */
            value: kpis.fleetScore == null ? '—' : bandLabel(publishedBand(kpis.fleetScore)),
            direction: 'flat', intent: 'neutral',
          }}
        />
        <MetricCard
          label="On probation"
          value={kpis.onProbation}
          icon={<Lock size={16} />}
          tone={kpis.onProbation > 0 ? 'danger' : 'muted'}
        />
        <MetricCard
          label="Active anomalies"
          value={kpis.anomalies}
          icon={<AlertTriangle size={16} />}
          tone={kpis.anomalies > 0 ? 'danger' : 'success'}
        />
      </div>

      {/* Distribution cards — three side-by-side */}
      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        <Card padding="md">
          <Card.Header title="Risk level distribution" subtitle="By trust-score-derived risk band" />
          <Card.Body>
            <div className="space-y-2">
              {[
                { key: 'LOW',      label: 'Low',      tone: 'success' },
                { key: 'MEDIUM',   label: 'Medium',   tone: 'warning' },
                { key: 'HIGH',     label: 'High',     tone: 'danger' },
                { key: 'CRITICAL', label: 'Critical', tone: 'danger' },
                { key: 'UNRATED',  label: 'Unrated',  tone: 'muted' },
              ].map((row) => {
                const n = riskDistribution[row.key] || 0;
                const pct = kpis.total > 0 ? Math.round((n / kpis.total) * 100) : 0;
                return (
                  <DistributionRow key={row.key} label={row.label} count={n} pct={pct} tone={row.tone} />
                );
              })}
            </div>
          </Card.Body>
        </Card>

        <Card padding="md">
          <Card.Header title="Autonomy distribution"
                       subtitle="How decision authority is distributed" />
          <Card.Body>
            {autonomyDistribution.length === 0 ? (
              <p className="text-v2-ink-muted text-[12.5px] italic">No autonomy levels recorded.</p>
            ) : (
              <div className="space-y-2">
                {autonomyDistribution.map(([level, n]) => {
                  const pct = kpis.total > 0 ? Math.round((n / kpis.total) * 100) : 0;
                  const tone = level === 'SELF_AUTONOMOUS' ? 'success'
                             : level === 'SUPERVISED' ? 'warning'
                             : level === 'RESTRICTED' ? 'warning'
                             : level === 'PROBATION' ? 'danger'
                             : 'cyan';
                  return (
                    <DistributionRow
                      key={level}
                      label={String(level).replace(/_/g, ' ')}
                      count={n} pct={pct} tone={tone}
                    />
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card padding="md">
          <Card.Header title="Score distribution"
                       subtitle="Bucketed across the agent fleet" />
          <Card.Body>
            <ScoreDistributionBars
              excellent={scoreBuckets.excellent}
              good={scoreBuckets.good}
              fair={scoreBuckets.fair}
              atRisk={scoreBuckets.atRisk}
            />
          </Card.Body>
        </Card>
      </div>

      {/* Recent autonomy transitions */}
      <Card padding="md">
        <Card.Header
          title="Recent autonomy transitions"
          subtitle="Most recent agent level changes (scoring engine + operator overrides)"
        />
        <Card.Body>
          {loading ? (
            <p className="text-v2-ink-muted text-[12.5px]">Loading…</p>
          ) : recentTransitions.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck size={18} />}
              title="No recent transitions"
              description="No autonomy changes recorded in the current window."
            />
          ) : (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-v2-border-subtle">
                  <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Agent</th>
                  <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Transition</th>
                  <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Trigger</th>
                  <th className="text-right text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {recentTransitions.map((t, i) => {
                  const ranks = { SELF_AUTONOMOUS: 4, SUPERVISED: 3, RESTRICTED: 2, HUMAN_CONTROLLED: 1, PROBATION: 0 };
                  const direction = (ranks[t.toLevel] ?? 0) - (ranks[t.fromLevel] ?? 0);
                  return (
                    <tr key={i} className="border-b border-v2-border-subtle/50">
                      <td className="py-2 font-semibold text-v2-ink">{t.agentName || t.agentId}</td>
                      <td className="py-2">
                        <span className="inline-flex items-center gap-1.5 text-[11.5px]">
                          <Pill tone="muted" size="sm">{(t.fromLevel || 'UNSET').replace(/_/g, ' ')}</Pill>
                          {direction > 0 ? <ArrowUpRight size={12} className="text-v2-success-strong" />
                           : direction < 0 ? <ArrowDownRight size={12} className="text-v2-danger-strong" />
                           : <span className="text-v2-ink-muted">→</span>}
                          <Pill tone={direction > 0 ? 'success' : direction < 0 ? 'danger' : 'muted'} size="sm">
                            {(t.toLevel || 'UNSET').replace(/_/g, ' ')}
                          </Pill>
                        </span>
                      </td>
                      <td className="py-2 text-v2-ink-secondary text-[11.5px]">
                        {t.triggerType || t.trigger || 'auto'}
                      </td>
                      <td className="py-2 text-right font-mono-v2 text-[11px] text-v2-ink-muted">
                        {t.timestamp ? new Date(t.timestamp).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>

      {/* Server-side tenant-wide dashboard (raw) */}
      {serverData && (
        <Card padding="md">
          <Card.Header
            title="Tenant-wide dashboard"
            subtitle="Server aggregates for the agents currently in scope"
          />
          <Card.Body>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12.5px]">
              {Object.entries(serverData).slice(0, 12).map(([k, v]) => (
                <div key={k} className="bg-v2-surface-muted rounded-v2-sm p-3">
                  <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">{k.replace(/([A-Z])/g, ' $1').trim()}</dt>
                  <dd className="text-v2-ink font-bold text-[16px] mt-1 m-0">
                    {typeof v === 'object'
                      ? <span className="text-[11.5px] font-mono-v2 text-v2-ink-secondary">{JSON.stringify(v).slice(0, 60)}</span>
                      : String(v)}
                  </dd>
                </div>
              ))}
            </dl>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

function DistributionRow({ label, count, pct, tone }) {
  const TONE_BG = {
    success: 'bg-v2-success',
    warning: 'bg-v2-warning',
    danger:  'bg-v2-danger',
    cyan:    'bg-v2-accent-cyan',
  };
  return (
    <div>
      <div className="flex items-baseline justify-between text-[12px] mb-1">
        <span className="font-medium text-v2-ink">{label}</span>
        <span className="text-v2-ink-muted font-mono-v2 text-[11.5px]">
          {count} <span className="opacity-60">· {pct}%</span>
        </span>
      </div>
      <div className="h-2 rounded-v2-pill bg-v2-surface-muted overflow-hidden">
        <div className={`h-full ${TONE_BG[tone] || 'bg-v2-action'} transition-[width] duration-300`}
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default DashboardTab;
